import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, FileText, Lock, ArrowRight, ClipboardList, Loader2, Copy, Trash2, FilePen } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge, Button, Card, CardContent, Label,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@higgins/ui';
import { ContextSelector } from '@/components/report-editor/context-selector';
import {
  reportsAtom,
  filterRegionAtom, filterBranchAtom, filterDivisionAtom, filterStatusAtom, filterMonthAtom,
} from '@/store/atoms';
import { useReports, useCreateReport, useCloneReport, useAmendReport, useDeleteReport } from '@/lib/hooks';
import { MONTHS, formatMonthYear } from '@/lib/types';
import type { Report, NewReportContext, ReportStatus } from '@/lib/types';
import { LAST_COMPLETED_MONTH, LAST_COMPLETED_YEAR, isMonthCompleted, completedMonths } from '@/lib/period';

const STATUS_FILTER_OPTIONS: ReportStatus[] = ['Draft', 'Published', 'Amended', 'Distributed'];

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

const STATUS_BADGE: Record<ReportStatus, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  Draft:       { label: 'Draft',       variant: 'secondary' },
  Published:   { label: 'Published',   variant: 'default'   },
  Amended:     { label: 'Amended',     variant: 'outline'   },
  Distributed: { label: 'Distributed', variant: 'default'   },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function truncateList(items: string[], max = 2): string {
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')} +${items.length - max} more`;
}

function nextMonth(month: number, year: number): { month: number; year: number } {
  return month === 12 ? { month: 1, year: year + 1 } : { month: month + 1, year };
}

// Default clone target: the month after the source, but never past the last completed
// month (clone must also target a completed period).
function cloneDefault(report: Report | null): { month: number; year: number } {
  const next = report ? nextMonth(report.month, report.year) : { month: LAST_COMPLETED_MONTH, year: LAST_COMPLETED_YEAR };
  return isMonthCompleted(next.year, next.month) ? next : { month: LAST_COMPLETED_MONTH, year: LAST_COMPLETED_YEAR };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [mockReports, setMockReports] = useAtom(reportsAtom);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<Report | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Report | null>(null);

  // Filters — persisted to localStorage via jotai
  const [filterRegion,   setFilterRegion]   = useAtom(filterRegionAtom);
  const [filterBranch,   setFilterBranch]   = useAtom(filterBranchAtom);
  const [filterDivision, setFilterDivision] = useAtom(filterDivisionAtom);
  const [filterStatus,   setFilterStatus]   = useAtom(filterStatusAtom);
  const [filterMonth,    setFilterMonth]    = useAtom(filterMonthAtom);

  // API-mode hooks
  const { data: apiReports, isLoading, isError } = useReports();
  const createMutation = useCreateReport();
  const cloneMutation  = useCloneReport();
  const amendMutation  = useAmendReport();
  const deleteMutation = useDeleteReport();

  // In mock mode, use local atom; otherwise use React Query data
  const reports: Report[] = IS_MOCK ? mockReports : (apiReports ?? []);

  // Derived filter options
  const uniqueRegions = useMemo(
    () => [...new Set(reports.map(r => r.region))].sort(),
    [reports],
  );
  const uniqueBranches = useMemo(() => {
    const src = filterRegion ? reports.filter(r => r.region === filterRegion) : reports;
    return [...new Set(src.flatMap(r => r.branches))].sort();
  }, [reports, filterRegion]);
  const uniqueDivisions = useMemo(() => {
    const src = filterRegion ? reports.filter(r => r.region === filterRegion) : reports;
    const branched = filterBranch ? src.filter(r => r.branches.includes(filterBranch)) : src;
    return [...new Set(branched.flatMap(r => r.divisions))].sort();
  }, [reports, filterRegion, filterBranch]);
  const uniquePeriods = useMemo(() => {
    const map = new Map<string, { year: number; month: number }>();
    for (const r of reports) map.set(`${r.year}-${r.month}`, { year: r.year, month: r.month });
    return [...map.values()].sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month));
  }, [reports]);

  // Filtered list
  const filteredReports = useMemo(() => reports.filter(r => {
    if (filterRegion   && r.region !== filterRegion)                 return false;
    if (filterBranch   && !r.branches.includes(filterBranch))        return false;
    if (filterDivision && !r.divisions.includes(filterDivision))     return false;
    if (filterStatus   && r.status !== filterStatus)                 return false;
    if (filterMonth    && `${r.year}-${r.month}` !== filterMonth)    return false;
    return true;
  }), [reports, filterRegion, filterBranch, filterDivision, filterStatus, filterMonth]);

  const hasFilters = !!(filterRegion || filterBranch || filterDivision || filterStatus || filterMonth);

  function clearFilters() {
    setFilterRegion('');
    setFilterBranch('');
    setFilterDivision('');
    setFilterStatus('');
    setFilterMonth('');
  }

  function handleRegionChange(val: string) {
    setFilterRegion(val === '__all__' ? '' : val);
    setFilterBranch('');
    setFilterDivision('');
  }
  function handleBranchChange(val: string) {
    setFilterBranch(val === '__all__' ? '' : val);
    setFilterDivision('');
  }
  function handleDivisionChange(val: string) {
    setFilterDivision(val === '__all__' ? '' : val);
  }
  function handleStatusChange(val: string) {
    setFilterStatus(val === '__all__' ? '' : val);
  }
  function handleMonthChange(val: string) {
    setFilterMonth(val === '__all__' ? '' : val);
  }

  async function handleCreate(context: NewReportContext) {
    setDialogOpen(false);

    if (IS_MOCK) {
      const monthName = MONTHS[context.month - 1];
      const id = `r-${Date.now()}`;
      const newReport: Report = {
        id,
        name: `${context.region} — ${monthName} ${context.year}`,
        region: context.region,
        branches: context.branches,
        divisions: context.divisions,
        month: context.month,
        year: context.year,
        monthYear: `${monthName} ${context.year}`,
        status: 'Draft',
        preparedBy: 'Damian Davies',
        createdOn: new Date().toISOString(),
        modifiedOn: new Date().toISOString(),
      };
      setMockReports(prev => [newReport, ...prev]);
      toast.success('Report created', {
        description: `${newReport.monthYear} — visuals will be fetched once the API is connected.`,
      });
      navigate(`/reports/${id}`);
    } else {
      try {
        const { id, populatedFrom, warning } = await createMutation.mutateAsync(context);
        if (warning) {
          toast.warning('Report created', { description: warning });
        } else {
          const seeded = populatedFrom
            ? `Commentary copied from ${populatedFrom}. `
            : context.populateFromPrevious
              ? `No earlier published or distributed report for ${context.region} — created blank. `
              : '';
          toast.success('Report created', { description: `${seeded}Fetching visuals…` });
        }
        navigate(`/reports/${id}`);
      } catch (e: any) {
        toast.error('Failed to create report', { description: e.message });
      }
    }
  }

  async function handleClone(month: number, year: number) {
    if (!cloneTarget) return;
    const source = cloneTarget;
    setCloneTarget(null);

    if (IS_MOCK) {
      const monthName = MONTHS[month - 1];
      const id = `r-${Date.now()}`;
      const cloned: Report = {
        ...source,
        id,
        month,
        year,
        monthYear: `${monthName} ${year}`,
        status: 'Draft',
        sourceReportId: source.id,
        publishedOn: undefined,
        publishedBy: undefined,
        createdOn: new Date().toISOString(),
        modifiedOn: new Date().toISOString(),
      };
      setMockReports(prev => [cloned, ...prev]);
      toast.success('Report cloned', { description: `${cloned.monthYear} — draft created.` });
      navigate(`/reports/${id}`);
    } else {
      try {
        const { id } = await cloneMutation.mutateAsync({ id: source.id, month, year });
        toast.success('Report cloned', { description: `${MONTHS[month - 1]} ${year} draft created.` });
        navigate(`/reports/${id}`);
      } catch (e: any) {
        toast.error('Failed to clone report', { description: e.message });
      }
    }
  }

  async function handleAmend(report: Report) {
    if (IS_MOCK) {
      toast.info('Amend', { description: 'Connect the API to create an editable draft of a published report.' });
      return;
    }
    try {
      const { id } = await amendMutation.mutateAsync(report.id);
      toast.success('Editable draft created', { description: `${report.monthYear} — amend the published figures and re-publish.` });
      navigate(`/reports/${id}`);
    } catch (e: any) {
      toast.error('Failed to create draft', { description: e.message });
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);

    if (IS_MOCK) {
      setMockReports(prev => prev.filter(r => r.id !== target.id));
      toast.success('Report deleted');
    } else {
      try {
        await deleteMutation.mutateAsync(target.id);
        toast.success('Report deleted');
      } catch (e: any) {
        toast.error('Failed to delete report', { description: e.message });
      }
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Reports</h2>
            <p className="text-sm text-slate-500 mt-1">
              {isLoading
                ? 'Loading…'
                : hasFilters
                  ? `${filteredReports.length} of ${reports.length} report${reports.length !== 1 ? 's' : ''}`
                  : `${reports.length} report${reports.length !== 1 ? 's' : ''}`
              }
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)} disabled={createMutation.isPending}>
            {createMutation.isPending
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Plus className="w-4 h-4 mr-2" />
            }
            New Report
          </Button>
        </div>

        {/* Filters */}
        {reports.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide mr-1">Filter</span>
            <Select value={filterRegion || '__all__'} onValueChange={handleRegionChange}>
              <SelectTrigger className="h-8 text-sm w-36">
                <SelectValue placeholder="All regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All regions</SelectItem>
                {uniqueRegions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterBranch || '__all__'} onValueChange={handleBranchChange}>
              <SelectTrigger className="h-8 text-sm w-40">
                <SelectValue placeholder="All branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All branches</SelectItem>
                {uniqueBranches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterDivision || '__all__'} onValueChange={handleDivisionChange}>
              <SelectTrigger className="h-8 text-sm w-40">
                <SelectValue placeholder="All divisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All divisions</SelectItem>
                {uniqueDivisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterMonth || '__all__'} onValueChange={handleMonthChange}>
              <SelectTrigger className="h-8 text-sm w-36">
                <SelectValue placeholder="All months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All months</SelectItem>
                {uniquePeriods.map(p => (
                  <SelectItem key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                    {formatMonthYear(p.month, p.year)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || '__all__'} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-8 text-sm w-36">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {STATUS_FILTER_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_BADGE[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </div>
        )}

        {isError && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
            Failed to load reports. Check your API connection.
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading reports…
          </div>
        ) : filteredReports.length === 0 && reports.length === 0 ? (
          <EmptyState onNew={() => setDialogOpen(true)} />
        ) : filteredReports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center gap-2">
            <p className="font-medium text-slate-700">No reports match these filters</p>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence initial={false}>
              {filteredReports.map((report, i) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.2, delay: i * 0.04 }}
                >
                  <ReportCard
                    report={report}
                    onAction={() => navigate(`/reports/${report.id}`)}
                    onClone={() => setCloneTarget(report)}
                    onAmend={() => handleAmend(report)}
                    onDelete={() => setDeleteTarget(report)}
                    amendPending={amendMutation.isPending}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      <ContextSelector
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleCreate}
      />

      <CloneDialog
        report={cloneTarget}
        onOpenChange={open => { if (!open) setCloneTarget(null); }}
        onClone={handleClone}
        isPending={cloneMutation.isPending}
      />

      <DeleteDialog
        report={deleteTarget}
        onOpenChange={open => { if (!open) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}

// ─── Report card ─────────────────────────────────────────────────────────────

function ReportCard({
  report,
  onAction,
  onClone,
  onAmend,
  onDelete,
  amendPending,
}: {
  report: Report;
  onAction: () => void;
  onClone: () => void;
  onAmend: () => void;
  onDelete: () => void;
  amendPending: boolean;
}) {
  const { label, variant } = STATUS_BADGE[report.status];
  const isPublished = report.status === 'Published';
  // Amend (creates an editable copy that retires the source) only applies to the
  // live Published version. Clone (copy forward to a new month) is also allowed
  // from a Distributed report, which is otherwise locked.
  const canAmend = report.status === 'Published';
  const canClone = report.status === 'Published' || report.status === 'Distributed';
  const isLocked = report.status === 'Published'
    || report.status === 'Amended'
    || report.status === 'Distributed';
  const isDeletable = report.status === 'Draft';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
              {isLocked
                ? <Lock className="w-4 h-4 text-muted-foreground" />
                : <FileText className="w-4 h-4 text-muted-foreground" />
              }
            </div>
            <div className="min-w-0 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">{report.monthYear}</span>
                <Badge variant={variant}>{label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground truncate">
                <span className="font-medium text-slate-600">{report.region}</span>
                {' • '}
                {truncateList(report.branches)}
                {' • '}
                {truncateList(report.divisions)}
              </p>
              <p className="text-xs text-muted-foreground">
                {isPublished && report.publishedOn
                  ? `Published ${formatDate(report.publishedOn)}`
                  : `Modified ${formatDate(report.modifiedOn)}`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canAmend && (
              <Button variant="ghost" size="sm" onClick={onAmend} disabled={amendPending} title="Amend (create editable copy)">
                {amendPending
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <FilePen className="w-3.5 h-3.5" />
                }
              </Button>
            )}
            {canClone && (
              <Button variant="ghost" size="sm" onClick={onClone} title="Clone to new month">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            )}
            {isDeletable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                title="Delete"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant={isLocked ? 'outline' : 'default'} size="sm" onClick={onAction}>
              {isLocked ? 'View' : 'Edit'}
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Delete dialog ────────────────────────────────────────────────────────────

function DeleteDialog({
  report,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  report: Report | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={!!report} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Draft</DialogTitle>
          <DialogDescription>
            {report
              ? `Permanently delete "${report.monthYear}"? This cannot be undone.`
              : 'This cannot be undone.'
            }
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Clone dialog ─────────────────────────────────────────────────────────────

function CloneDialog({
  report,
  onOpenChange,
  onClone,
  isPending,
}: {
  report: Report | null;
  onOpenChange: (open: boolean) => void;
  onClone: (month: number, year: number) => void;
  isPending: boolean;
}) {
  const defaultNext = cloneDefault(report);
  const [month, setMonth] = useState(defaultNext.month);
  const [year,  setYear]  = useState(defaultNext.year);

  // Reset defaults when a new source report is selected
  const [lastReportId, setLastReportId] = useState<string | null>(null);
  if (report && report.id !== lastReportId) {
    const next = cloneDefault(report);
    setMonth(next.month);
    setYear(next.year);
    setLastReportId(report.id);
  }

  return (
    <Dialog open={!!report} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Clone Report</DialogTitle>
          <DialogDescription>
            {report
              ? `Copy commentary from ${report.monthYear} to a new draft.`
              : 'Copy commentary to a new draft.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 py-1">
          <Label>Report month</Label>
          <Select
            value={`${year}-${month}`}
            onValueChange={v => { const [y, m] = v.split('-').map(Number); setYear(y); setMonth(m); }}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {completedMonths().map(p => (
                <SelectItem key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                  {formatMonthYear(p.month, p.year)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onClone(month, year)} disabled={isPending}>
            {isPending
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Copy className="w-4 h-4 mr-2" />
            }
            Clone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-20 text-center gap-4"
    >
      <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
        <ClipboardList className="w-8 h-8 text-orange-400" />
      </div>
      <div className="space-y-1">
        <p className="font-semibold text-slate-900">No reports yet</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Create your first GM report to get started.
        </p>
      </div>
      <Button onClick={onNew}>
        <Plus className="w-4 h-4 mr-2" />
        New Report
      </Button>
    </motion.div>
  );
}
